import React from 'react';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { Session } from 'state';
import { StatusFilter, TypeFilter, statusMeta, toCardModel } from './helpers';

type Counts = { all: number; a: number; b: number };

type Props = {
  totalCount: number;
  query: string;
  onQuery: (q: string) => void;
  status: StatusFilter;
  onStatus: (s: StatusFilter) => void;
  statusCounts: Counts; // all / active / finished
  type: TypeFilter;
  onType: (t: TypeFilter) => void;
  typeCounts: Counts; // all / historical / realtime
  list: Session[]; // fully filtered + sorted, for the quick-nav list
  focusedId: number | null;
  onFocus: (id: number) => void;
};

type Pill = { key: string; label: string; count: number };

function PillRow<T extends string>({
  value,
  onChange,
  pills,
}: {
  value: T;
  onChange: (v: T) => void;
  pills: Pill[];
}) {
  return (
    <div className="dv-filters">
      {pills.map(p => {
        const active = value === (p.key as T);
        return (
          <button
            key={p.key}
            type="button"
            className={`dv-pill${active ? ' is-on' : ''}`}
            aria-pressed={active}
            onClick={() => onChange(p.key as T)}
          >
            {p.label}
            <span className="dv-pill__c">{p.count}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The single-user command-center sidecar: search + status/type filter pills +
 * a quick-nav list of the trainee's own sessions. Borrows the Trainer rail's
 * STYLE (search, count pill, chip filters, active highlight) but none of its
 * multi-class hierarchy — there is one flat stream to filter.
 */
const Sidecar: React.FC<Props> = ({
  totalCount,
  query,
  onQuery,
  status,
  onStatus,
  statusCounts,
  type,
  onType,
  typeCounts,
  list,
  focusedId,
  onFocus,
}) => (
  <aside className="dv-rail">
    <div className="dv-rail__head">
      <div className="dv-rail__title">
        Sessions <span className="dv-count">{totalCount}</span>
      </div>
      <Input
        allowClear
        size="small"
        className="dv-search"
        prefix={<SearchOutlined />}
        placeholder="Search class, trainer, #id…"
        value={query}
        onChange={e => onQuery(e.target.value)}
      />
      <PillRow<StatusFilter>
        value={status}
        onChange={onStatus}
        pills={[
          { key: 'all', label: 'All', count: statusCounts.all },
          { key: 'active', label: 'Active', count: statusCounts.a },
          { key: 'finished', label: 'Finished', count: statusCounts.b },
        ]}
      />
      <PillRow<TypeFilter>
        value={type}
        onChange={onType}
        pills={[
          { key: 'all', label: 'All types', count: typeCounts.all },
          { key: 'historical', label: 'Historical', count: typeCounts.a },
          { key: 'realtime', label: 'Real-time', count: typeCounts.b },
        ]}
      />
    </div>

    <div className="dv-rail__list">
      {list.length === 0 ? (
        <div className="dv-rail__empty">
          {query ? `No sessions match “${query}”.` : 'No sessions to show.'}
        </div>
      ) : (
        list.map(s => {
          const m = toCardModel(s);
          const tone = statusMeta(s.status).tone;
          return (
            <button
              key={s.id}
              type="button"
              className={`dv-mini${focusedId === s.id ? ' is-focus' : ''}`}
              onClick={() => onFocus(s.id)}
            >
              <span className={`dv-mini__dot dv-mini__dot--${tone}`} />
              <span className="dv-mini__id">#{s.id}</span>
              <span className="dv-mini__name" title={m.title}>
                {m.title}
              </span>
            </button>
          );
        })
      )}
    </div>
  </aside>
);

export default Sidecar;
